/* eslint-disable no-alert */
import React, { useState, useCallback } from "react";
import _get from "lodash/get";
import { useSelector, useDispatch } from "react-redux";
import { EyeIcon, PencilIcon, XIcon } from "@heroicons/react/outline";
import { CheckIcon, PlusIcon, MinusIcon } from "@heroicons/react/solid";
import { useHistory } from "react-router-dom";

import Table from "components/lib/Global/ReactTable";
import TableActionMenu from "components/lib/Shared/TableActionMenu";
import CommentsPopup from "components/lib/Global/CommentsPopup";
import SpinnerIcon from "components/lib/Shared/Icons/SpinnerIcon";
import { deleteNominalShareAsync } from "state/slices/tables/nominalSharesTable";
import { resolveFields, invalidateFields } from "state/slices/resolveField";
import { roundNumber } from "utils";
import { getComments } from "state/slices/comment";
import { deleteTemplateAsync } from "state/slices/template";
import TableSubRow from "./TableSubRow";

const processTypeToChangeType = {
  sharesConsolidation: "SHARES-CONSOLIDATION",
  sharesSubdivision:   "SHARES-SUBDIVISION",
};

const NominalSharesTable = (props) => {
  const { company } = useSelector((state) => state.companySlice);
  const sharesFromState = useSelector((state) => state.sharesTableSlice.shares);
  const rawShares = props?.nominalShares || sharesFromState || [];
  const shares = rawShares.filter(
    (share) => share?.sharesNumber && share.sharesNumber !== 0
  );
  const { resolvedFields } = useSelector((state) => state.companySlice);

  // Pull initiated transactions to detect existing consolidation/subdivision entries
  const { initiatedTransactions } = useSelector(
    (state) => state.initiatedTransactionsSlice
  );

  const [isLoading, setIsLoading] = useState(false);
  const [selectedRows, setSelectedRow] = useState([]);
  const [allFieldsResolved, setAllFieldsResolved] = useState(false);
  const dispatch = useDispatch();
  const history = useHistory();
  const currentUser = useSelector((state) => state.userSlice);
  const isViewMode = history.location.pathname.split("/").pop() === "view";
  const isReviewerMode =
    history.location.pathname.split("/").pop() === "review";
  const [expandedRow, setExpandedRow] = useState({});
  const [initialRows, setInitialRows] = useState({});

  // Returns true when the given share class already has a proposed
  // consolidation / subdivision entry in any DRAFT transaction.
  const hasProposedReorgForClass = (sharesCategory) => {
    if (
      props.changeType !== "sharesConsolidation" &&
      props.changeType !== "sharesSubdivision"
    ) {
      return false;
    }
    const txChangeType = processTypeToChangeType[props.changeType];
    return (initiatedTransactions?.items || []).some(
      (tx) =>
        tx.changeType === txChangeType &&
        tx.status === "DRAFT" &&
        (tx.shareCapitalChanges || []).some(
          (c) => c.classOfShares === sharesCategory
        )
    );
  };

  const closeSubRow = (row) => {
    setInitialRows({});
    setExpandedRow({});
    row.toggleRowExpanded();
  };

  const handleAuthorizationEdit = (row, rest) => {
    if (!row.isExpanded) {
      row.toggleAllRowsExpanded(false);
      setInitialRows(rest.initialRows);
      setExpandedRow(row);
      row.toggleRowExpanded();
      props.setMode({ editing: true, id: row.original._id });
    } else {
      setInitialRows({});
      setExpandedRow({});
      row.toggleRowExpanded();
    }
  };

  const handleEdit = (row) => props.handleEdit(row);

  const handleDelete = async (row) => {
    await dispatch(deleteNominalShareAsync({ id: row._id }));
    dispatch(deleteTemplateAsync({ templateId: row.rightsAttached._id }));
  };

  const handleResolveBtnClicked = async (fieldName) => {
    const commentsToBeResolved = props.comments?.filter(
      (comment) => comment.fieldName === fieldName && !comment.isResolved
    );
    const commentsToBeResolvedIds =
      commentsToBeResolved?.map(({ _id }) => _id) ?? [];
    setIsLoading(true);
    setSelectedRow((prev) => [...prev, fieldName]);
    const promise = await dispatch(
      resolveFields({
        data: {
          formId: _get(company, "_id"),
          formName: "company",
          fieldName,
          resolvedFields: [fieldName],
          commentsToBeResolvedIds,
        },
        updateEffectKey: "updateCompanyState",
      })
    );
    if (commentsToBeResolvedIds.length > 0) {
      const query = { companyId: commentsToBeResolved[0]?.company };
      if (isReviewerMode) query.createdBy = currentUser?._id;
      await dispatch(getComments(query));
    }
    setIsLoading(false);
    setSelectedRow((prev) =>
      prev.filter((n) => n !== promise.meta.arg.data.fieldName)
    );
  };

  const handleInvalidateBtnClicked = async (fieldName) => {
    setIsLoading(true);
    setSelectedRow((prev) => [...prev, fieldName]);
    const promise = await dispatch(
      invalidateFields({
        data: {
          formId: _get(company, "_id"),
          formName: "company",
          fieldName,
          invalidFields: [fieldName],
        },
        updateEffectKey: "updateCompanyState",
      })
    );
    setIsLoading(false);
    setSelectedRow((prev) =>
      prev.filter((n) => n !== promise.meta.arg.data.fieldName)
    );
  };

  const sumOfVotingRights = shares.reduce(
    (acc, share) => roundNumber(acc + Number(share.votingRights)),
    0
  );

  const data = [...shares];
  if (shares.length > 0) {
    data.push({
      id: "total",
      sharesCategory: "Total",
      sharesNumber: "",
      sharesValue: "",
      nominalValue: "",
      votingRights: sumOfVotingRights,
    });
  }

  const columns = [
    {
      Header: "Share Category",
      Cell: ({ row }) => {
        if (row.original.id === "total") return <b>Total</b>;
        return row.original.sharesCategory;
      },
    },
    { Header: "Number of Shares", accessor: "sharesNumber" },
    { Header: "Value of Shares",  accessor: "sharesValue" },
    { Header: "Nominal Value",    accessor: "nominalValue" },
    {
      Header: "Voting Rights",
      Cell: ({ row }) => {
        if (row.original.id === "total")
          return <b>{row.original.votingRights}</b>;
        return row.original.votingRights;
      },
    },
  ];

  if (props.showActionColumn && !allFieldsResolved) {
    columns.push({
      Header: "Actions",
      Cell: ({ row, ...rest }) => {
        if (row.original.id === "total") return "";

        const hasMatchingAuthorization = (props.authorizations || [])
          .filter((auth) => !auth.isApproved)
          .some((auth) => {
            const authClass = auth.classOfShares || auth.sharesCategory;
            const rowClass  = row.original?.sharesCategory;
            return authClass && rowClass && authClass === rowClass;
          });
        const hasmatchingAllotment = props?.allotments?.some(
          (allotment) =>
            allotment.ProposalData?.sharesCategory ===
            row.original?.sharesCategory
        );
        const isUnallotedShares =
          props.changeType === "unallotedShares" ||
          props.changeType === "shareTransfer" ||
          props.changeType === "forfeitureOfShares" ||
          props.changeType === "transmissionOfShares" ||
          props.changeType === "PVTwithOneClass";
        const allowedChangeTypes = [
          "authorization",
          "privateWithMoreThanOneClass",
          "privateWithOneClass",
        ];

        const ViewOnlyContent = (
          <div className="flex items-center space-x-2" />
        );

        const AuthEditButtonsContent = (
          <div className="flex items-center space-x-2">
            <button
              onClick={() => props.handleView(row.original)}
              className="flex space-x-1 justify-center items-center focus:outline-none mr-5 hover:text-gray-600 py-2.5 text-sm leading-3 text-gray-500"
            >
              <EyeIcon className="w-6 h-6" />
              <span>Quick View</span>
            </button>
            {row?.isExpanded ? (
              <button onClick={() => handleAuthorizationEdit(row, rest)}>
                Close
              </button>
            ) : (
              <TableActionMenu
                row={row}
                handleEdit={() => handleAuthorizationEdit(row, rest)}
                hideDeleteBtn={true}
              />
            )}
          </div>
        );

        const StandardEditButtonsContent = (
          <div className="flex items-center space-x-2">
            <button
              onClick={() => props.handleView(row.original)}
              className="flex space-x-1 justify-center items-center focus:outline-none mr-5 hover:text-gray-600 py-2.5 text-sm leading-3 text-gray-500"
            >
              <EyeIcon className="w-6 h-6" />
              <span>Quick View</span>
            </button>
            <TableActionMenu
              row={row}
              handleEdit={handleEdit}
              handleDelete={handleDelete}
            />
          </div>
        );

        const isReorgChangeType =
          props.changeType === "sharesConsolidation" ||
          props.changeType === "sharesSubdivision";

        if (props.isInChangesModule && isReorgChangeType) {
          const label =
            props.changeType === "sharesConsolidation"
              ? "Consolidate"
              : "Sub-Divide";

          // Hide the action button when this class already has a proposed entry
          const alreadyProposed = hasProposedReorgForClass(
            row.original.sharesCategory
          );
          if (alreadyProposed) return ViewOnlyContent;

          return (
            <div className="flex items-center space-x-2">
              <span
                onClick={() => handleAuthorizationEdit(row, rest)}
                className="text-sm font-medium cursor-pointer text-gray-700 hover:text-sflPrimary"
              >
                {label}
              </span>
              <button
                onClick={() => handleAuthorizationEdit(row, rest)}
                className="inline-flex items-center justify-center w-6 h-6 text-sm font-medium text-white bg-sflPrimary rounded-full shadow-sm hover:bg-optional hover:text-sflPrimary focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-sflPrimary"
                title={row.isExpanded ? `Close ${label}` : label}
              >
                {row.isExpanded ? (
                  <MinusIcon className="w-3.5 h-3.5" />
                ) : (
                  <PlusIcon className="w-3.5 h-3.5" />
                )}
              </button>
            </div>
          );
        }

        if (allowedChangeTypes.includes(props.changeType)) {
          if (hasMatchingAuthorization || hasmatchingAllotment) {
            return ViewOnlyContent;
          }
          return AuthEditButtonsContent;
        }

        if (props.isInChangesModule) {
          if (
            hasMatchingAuthorization ||
            isUnallotedShares ||
            hasmatchingAllotment
          ) {
            return ViewOnlyContent;
          }
          return AuthEditButtonsContent;
        }

        if (
          hasMatchingAuthorization ||
          isUnallotedShares ||
          hasmatchingAllotment
        ) {
          return ViewOnlyContent;
        }
        return StandardEditButtonsContent;
      },
    });
  }

  if (props.showAddCommentColumn && !isViewMode) {
    columns.push({
      Header: "Actions",
      Cell: ({ row }) => {
        if (row.original.id === "total") return "";
        const isRowLoading =
          isLoading && selectedRows.includes(row.original.sharesCategory);

        return (
          <div className="flex items-center space-x-2">
            {!resolvedFields.includes(row.original.sharesCategory) && (
              <CommentsPopup
                handleSaveComment={props.handleSaveComment}
                formName="shareInformation"
                fieldName={row.original.sharesCategory}
                fieldLabel={row.original.sharesCategory}
                comment={props.comments.find(
                  (comment) =>
                    comment.fieldName === row.original.sharesCategory &&
                    currentUser?._id === comment.createdBy?._id
                )}
              >
                <span>Review</span>
                <PencilIcon className="inline w-4 h-4" />
              </CommentsPopup>
            )}
            <div className="flex items-center justify-center">
              {resolvedFields.includes(row.original.sharesCategory) ? (
                <button
                  className={`flex space-x-1 ${
                    isRowLoading ? "cursor-not-allowed" : "cursor-pointer"
                  }`}
                  onClick={() =>
                    isRowLoading
                      ? null
                      : handleInvalidateBtnClicked(row.original.sharesCategory)
                  }
                >
                  {isRowLoading ? (
                    <SpinnerIcon className="text-primary" />
                  ) : (
                    <>
                      <span>Accepted </span>
                      <XIcon className="inline w-4 h-4" />
                    </>
                  )}
                </button>
              ) : (
                <button
                  className={`flex space-x-1 ${
                    isRowLoading ? "cursor-not-allowed" : "cursor-pointer"
                  }`}
                  onClick={() =>
                    isRowLoading
                      ? null
                      : handleResolveBtnClicked(row.original.sharesCategory)
                  }
                >
                  {isRowLoading ? (
                    <SpinnerIcon className="text-primary" />
                  ) : (
                    <>
                      <span>Accept</span>
                      <CheckIcon className="inline w-4 h-4" />
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        );
      },
    });
  }

  const renderRowSubComponent = useCallback(
    ({ row, rowProps, visibleColumns }) => (
      <TableSubRow
        changeType={props.changeType}
        row={row}
        filteredOfficials={props.filteredOfficials}
        companyId={props.companyId}
        mode={props.mode}
        rowProps={rowProps}
        visibleColumns={visibleColumns}
        initialRows={initialRows}
        takenShares={props.takenShares}
        closeSubRow={() => closeSubRow(row)}
        nominalShares={shares}
        isProposed={false}
        onSave={props.onSave}
      />
    ),
    [initialRows]
  );

  return (
    <div className="w-full bg-white shadow rounded">
      {props.isInChangesModule ? (
        <h1 className="text-md leading-6 p-3 font-medium text-gray-900">
          Current
        </h1>
      ) : null}
      <div className="w-full">
        <Table
          columns={columns}
          data={data}
          renderRowSubComponent={renderRowSubComponent}
        />
      </div>
    </div>
  );
};

export default NominalSharesTable;